import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import { useAuth } from "../../../context/AuthContext";
import AuthService from "./AuthService";
import type {
    LoginRequest,
    LoginResponse,
    RegisterRequest,
    ChangePasswordRequest,
    Uzivatel,
    ActiveSessionResponse,
    DeviceResponse,
    UpdateDeviceRequest,
    UserInfo,
} from "../generated";

// -----------------------------
// LOGIN MUTATION
// -----------------------------
export const useLoginMutation = () => {
    const { showToast } = useToast();
    const { login } = useAuth();

    return useMutation<LoginResponse, Error, LoginRequest>({
        mutationFn: (credentials) => AuthService.login(credentials),

        onSuccess: (response) => {
            login(response);
            showToast("Přihlášení bylo úspěšné.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Přihlášení se nezdařilo.", "error");
        },
    });
};

// -----------------------------
// LOGOUT MUTATION
// -----------------------------
export const useLogoutMutation = () => {
    const { showToast } = useToast();
    const { logout, refreshToken } = useAuth();
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: () => {
            if (refreshToken) {
                return AuthService.logout(refreshToken);
            }
            return Promise.resolve();
        },

        onSuccess: () => {
            logout();
            queryClient.clear(); // Clear all cached queries
            showToast("Odhlášení bylo úspěšné.", "success");
        },

        onError: () => {
            // Even if the server call fails, we should log out locally
            logout();
            queryClient.clear();
            showToast("Byli jste odhlášeni.", "success");
        },
    });
};

// -----------------------------
// LOGOUT ALL MUTATION
// -----------------------------
export const useLogoutAllMutation = () => {
    const { showToast } = useToast();
    const { logout } = useAuth();
    const queryClient = useQueryClient();

    return useMutation<void, Error, void>({
        mutationFn: () => AuthService.logoutAll(),

        onSuccess: () => {
            logout();
            queryClient.clear();
            showToast("Byli jste odhlášeni ze všech zařízení.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Odhlášení ze všech zařízení se nezdařilo.", "error");
        },
    });
};

// -----------------------------
// SESSIONS QUERY
// -----------------------------
export const useSessionsQuery = () => {
    return useQuery<ActiveSessionResponse[], Error>({
        queryKey: ["sessions"],
        queryFn: () => AuthService.getSessions(),
    });
};

// -----------------------------
// DEVICES QUERY
// -----------------------------
export const useDevicesQuery = () => {
    return useQuery<DeviceResponse[], Error>({
        queryKey: ["devices"],
        queryFn: () => AuthService.getDevices(),
    });
};

// -----------------------------
// UPDATE DEVICE MUTATION
// -----------------------------
export const useUpdateDeviceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<DeviceResponse, Error, { deviceId: number; data: UpdateDeviceRequest }>({
        mutationFn: ({ deviceId, data }) => AuthService.updateDevice(deviceId, data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["devices"] });
            showToast("Zařízení bylo přejmenováno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Přejmenování zařízení se nezdařilo.", "error");
        },
    });
};

// -----------------------------
// DELETE DEVICE MUTATION
// -----------------------------
export const useDeleteDeviceMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (deviceId) => AuthService.deleteDevice(deviceId),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["devices"] });
            showToast("Zařízení bylo odstraněno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Odstranění zařízení se nezdařilo.", "error");
        },
    });
};

// -----------------------------
// DELETE SESSION MUTATION (deprecated - use useDeleteDeviceMutation)
// -----------------------------
export const useDeleteSessionMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<void, Error, number>({
        mutationFn: (deviceId) => AuthService.deleteDevice(deviceId),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sessions"] });
            queryClient.invalidateQueries({ queryKey: ["devices"] });
            showToast("Session byla ukončena.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Ukončení session se nezdařilo.", "error");
        },
    });
};

// -----------------------------
// REGISTER MUTATION
// -----------------------------
export const useRegisterMutation = () => {
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    return useMutation<Uzivatel, Error, RegisterRequest>({
        mutationFn: (data) => AuthService.register(data),

        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
            showToast("Uživatel byl úspěšně zaregistrován.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Registrace se nezdařila.", "error");
        },
    });
};

// -----------------------------
// CHANGE PASSWORD MUTATION
// -----------------------------
export const useChangePasswordMutation = () => {
    const { showToast } = useToast();

    return useMutation<void, Error, ChangePasswordRequest>({
        mutationFn: (data) => AuthService.changePassword(data),

        onSuccess: () => {
            showToast("Heslo bylo úspěšně změněno.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Změna hesla se nezdařila.", "error");
        },
    });
};

// -----------------------------
// FORGOT PASSWORD MUTATION
// -----------------------------
export const useForgotPasswordMutation = () => {
    return useMutation<{ message: string }, Error, { email: string }>({
        mutationFn: ({ email }) => AuthService.forgotPassword(email),
    });
};

// -----------------------------
// SET PIN MUTATION
// -----------------------------
export const useSetPinMutation = () => {
    const { showToast } = useToast();
    const { updateUser } = useAuth();

    return useMutation<void, Error, string>({
        mutationFn: (pin) => AuthService.setPin(pin),

        onSuccess: async () => {
            showToast("PIN byl nastaven.", "success");
            try {
                const user = await AuthService.getCurrentUser();
                updateUser(user as unknown as UserInfo);
            } catch {
                // Refetch selhal, ale PIN byl nastaven úspěšně
            }
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se nastavit PIN.", "error");
        },
    });
};

// -----------------------------
// SET DATUM NAROZENI MUTATION (self-service, HO-22)
// -----------------------------
export const useSetDatumNarozeniMutation = () => {
    const { showToast } = useToast();

    return useMutation<{ success: boolean }, Error, string>({
        mutationFn: (datumNarozeni) => AuthService.setDatumNarozeni(datumNarozeni),

        onSuccess: () => {
            showToast("Datum narození bylo uloženo.", "success");
        },

        onError: (error) => {
            showToast(error.message || "Nepodařilo se uložit datum narození.", "error");
        },
    });
};
